import { axiosInstance, type CustomResponseFormat } from "api/instance";
import {
  isTaskEditorState,
  normalizeTaskEditorState,
  normalizePublishedTaskEditorTask,
  type PublishedTaskEditorTask,
} from "pages/task-editor/utils/taskEditorRuntime";
import type { TaskEditorState } from "pages/task-editor/utils/taskEditorUtils";

const TASK_EDITOR_DOMAIN = "task-editor";

const TASK_EDITOR_PUBLISHED_LIST_ENDPOINT = "published/list";
const TASK_EDITOR_PUBLISHED_SAVE_ENDPOINT = "published/save";
const TASK_EDITOR_PUBLISHED_REMOVE_ENDPOINT = "published/remove";
const TASK_EDITOR_WORKING_DRAFT_LOAD_ENDPOINT = "draft/load";
const TASK_EDITOR_WORKING_DRAFT_SAVE_ENDPOINT = "draft/save";
const TASK_EDITOR_DRAFT_LIST_ENDPOINT = "draft/list";
const TASK_EDITOR_DRAFT_REMOVE_ENDPOINT = "draft/remove";
const TASK_EDITOR_AUTH_HEADER_KEY = "X-Auth-Token";
const TASK_EDITOR_ACCESS_TOKEN_REQUIRED_ERROR =
  "taskEditorApi requires user access token.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

function unwrapResponsePayload<T>(
  response: CustomResponseFormat<T> | undefined
): unknown {
  if (!response) {
    return undefined;
  }

  const result = response.result;
  if (isRecord(result) && "data" in result) {
    return (result as Record<string, unknown>).data;
  }
  if (result !== undefined) {
    return result;
  }
  return response.data;
}

function normalizeTaskEditorTimestamp(value: unknown) {
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value).toISOString();
  }

  if (!isRecord(value)) {
    return undefined;
  }

  const epochSecond = value.epochSecond;
  const nano = value.nano;

  if (typeof epochSecond !== "number" || !Number.isFinite(epochSecond)) {
    return undefined;
  }

  const nanoToMillis =
    typeof nano === "number" && Number.isFinite(nano)
      ? Math.floor(nano / 1_000_000)
      : 0;

  return new Date(epochSecond * 1000 + nanoToMillis).toISOString();
}

function normalizeTaskEditorUserId(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.floor(value);
  }

  if (typeof value === "string") {
    const normalizedValue = Number(value.trim());

    if (Number.isFinite(normalizedValue)) {
      return Math.floor(normalizedValue);
    }
  }

  return undefined;
}

function extractTaskEditorPayload(payload: unknown): unknown {
  if (!isRecord(payload)) {
    return payload;
  }

  if (isRecord(payload.task)) {
    return {
      ...payload.task,
      ...(typeof payload.task_id === "string"
        ? { task_id: payload.task_id }
        : {}),
    };
  }

  if (isRecord(payload.draft)) {
    return payload.draft;
  }

  return payload;
}

function normalizeTaskEditorStatePayload(
  payload: unknown
): TaskEditorState | undefined {
  if (!isRecord(payload)) {
    return undefined;
  }

  return normalizeTaskEditorState(payload);
}

function normalizeTaskDraftMapPayload(
  payload: unknown
): Record<string, TaskEditorState> {
  const appendDraftState = (
    draftsByTaskId: Record<string, TaskEditorState>,
    draftTaskId: string,
    draftPayload: unknown
  ) => {
    const normalizedDraftTaskId = draftTaskId.trim();
    if (!normalizedDraftTaskId) {
      return draftsByTaskId;
    }

    const normalizedDraftPayload = normalizeTaskEditorStatePayload(
      extractTaskEditorPayload(draftPayload)
    );

    if (!normalizedDraftPayload) {
      return draftsByTaskId;
    }

    return {
      ...draftsByTaskId,
      [normalizedDraftTaskId]: normalizeTaskEditorState({
        ...normalizedDraftPayload,
        task_id: normalizedDraftTaskId,
      }),
    };
  };

  if (Array.isArray(payload)) {
    return payload.reduce<Record<string, TaskEditorState>>(
      (draftsByTaskId, item) => {
        if (!isRecord(item)) {
          return draftsByTaskId;
        }

        if (typeof item.task_id === "string" && isRecord(item.draft)) {
          return appendDraftState(draftsByTaskId, item.task_id, item.draft);
        }

        if (typeof item.task_id === "string") {
          return appendDraftState(draftsByTaskId, item.task_id, item);
        }

        return draftsByTaskId;
      },
      {}
    );
  }

  if (!isRecord(payload)) {
    return {};
  }

  return Object.entries(payload).reduce<Record<string, TaskEditorState>>(
    (draftsByTaskId, [task_id, draftPayload]) =>
      appendDraftState(draftsByTaskId, task_id, draftPayload),
    {}
  );
}

function normalizePublishedTaskPayload(payload: unknown) {
  const normalizedLegacyTask = normalizePublishedTaskEditorTask(payload);
  if (normalizedLegacyTask) {
    return normalizedLegacyTask;
  }

  const rawTaskPayload = extractTaskEditorPayload(payload);
  const normalizedStatePayload =
    normalizeTaskEditorStatePayload(rawTaskPayload);

  if (!normalizedStatePayload) {
    return undefined;
  }

  const envelope = isRecord(payload) ? payload : undefined;
  const updatedAt =
    normalizeTaskEditorTimestamp(envelope?.updatedAt) ??
    (isRecord(rawTaskPayload)
      ? normalizeTaskEditorTimestamp(rawTaskPayload.updatedAt)
      : undefined) ??
    new Date().toISOString();
  const versionId =
    (typeof envelope?.versionId === "string"
      ? envelope.versionId
      : undefined) ??
    (isRecord(rawTaskPayload) && typeof rawTaskPayload.versionId === "string"
      ? rawTaskPayload.versionId
      : updatedAt);
  const updatedByUserId =
    normalizeTaskEditorUserId(envelope?.updatedByUserId) ??
    normalizeTaskEditorUserId(envelope?.userId) ??
    (isRecord(rawTaskPayload)
      ? (normalizeTaskEditorUserId(rawTaskPayload.updatedByUserId) ??
        normalizeTaskEditorUserId(rawTaskPayload.userId))
      : undefined);

  return {
    ...normalizeTaskEditorState(normalizedStatePayload),
    updatedAt,
    versionId,
    ...(updatedByUserId !== undefined ? { updatedByUserId } : {}),
  };
}

function normalizePublishedTaskListPayload(
  payload: unknown
): PublishedTaskEditorTask[] {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload
    .map((task) => normalizePublishedTaskPayload(task))
    .filter((task): task is PublishedTaskEditorTask => Boolean(task));
}

function getTaskEditorAccessTokenFromStorage() {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) {
    return undefined;
  }

  try {
    const parsedUser = JSON.parse(storedUser);
    if (
      isRecord(parsedUser) &&
      typeof parsedUser.accessToken === "string" &&
      parsedUser.accessToken.trim().length > 0
    ) {
      return parsedUser.accessToken;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function getTaskEditorAccessTokenFromAxiosDefaults() {
  const headerToken =
    axiosInstance.defaults.headers?.[TASK_EDITOR_AUTH_HEADER_KEY];

  return typeof headerToken === "string" && headerToken.trim().length > 0
    ? headerToken
    : undefined;
}

function requireTaskEditorAccessToken() {
  const accessToken =
    getTaskEditorAccessTokenFromStorage() ??
    getTaskEditorAccessTokenFromAxiosDefaults();

  if (!accessToken) {
    throw new Error(TASK_EDITOR_ACCESS_TOKEN_REQUIRED_ERROR);
  }

  return accessToken;
}

function getTaskEditorAuthConfig(config?: {
  params?: Record<string, unknown>;
  headers?: Record<string, unknown>;
}) {
  const accessToken = requireTaskEditorAccessToken();

  return {
    ...config,
    headers: {
      ...(config?.headers ?? {}),
      [TASK_EDITOR_AUTH_HEADER_KEY]: accessToken,
    },
  };
}

export const taskEditorApi = {
  getPublishedTasks: async (): Promise<PublishedTaskEditorTask[]> => {
    const response = await axiosInstance.get<CustomResponseFormat<unknown>>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_PUBLISHED_LIST_ENDPOINT}`,
      getTaskEditorAuthConfig()
    );

    return normalizePublishedTaskListPayload(unwrapResponsePayload(response));
  },

  savePublishedTask: async (
    state: TaskEditorState,
    currentTaskId?: string
  ): Promise<PublishedTaskEditorTask | undefined> => {
    const response = await axiosInstance.post<CustomResponseFormat<unknown>>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_PUBLISHED_SAVE_ENDPOINT}`,
      {
        task: state,
      },
      getTaskEditorAuthConfig({
        params: {
          ...(currentTaskId ? { currentTaskId } : {}),
        },
      })
    );

    return normalizePublishedTaskPayload(unwrapResponsePayload(response));
  },

  removePublishedTask: async (task_id: string) => {
    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_PUBLISHED_REMOVE_ENDPOINT}`,
      undefined,
      getTaskEditorAuthConfig({
        params: { task_id },
      })
    );
  },

  getWorkingDraft: async (): Promise<TaskEditorState | undefined> => {
    const response = await axiosInstance.get<CustomResponseFormat<unknown>>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_WORKING_DRAFT_LOAD_ENDPOINT}`,
      getTaskEditorAuthConfig()
    );
    const payload = unwrapResponsePayload(response);

    if (isTaskEditorState(payload)) {
      return normalizeTaskEditorState(payload);
    }

    const normalizedPayload = normalizeTaskEditorStatePayload(
      extractTaskEditorPayload(payload)
    );

    return normalizedPayload
      ? normalizeTaskEditorState(normalizedPayload)
      : undefined;
  },

  saveWorkingDraft: async (state: TaskEditorState) => {
    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_WORKING_DRAFT_SAVE_ENDPOINT}`,
      {
        draft: state,
      },
      getTaskEditorAuthConfig()
    );
  },

  getTaskDraftsByTaskId: async (): Promise<Record<string, TaskEditorState>> => {
    const authConfig = getTaskEditorAuthConfig();

    try {
      const response = await axiosInstance.get<CustomResponseFormat<unknown>>(
        `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_DRAFT_LIST_ENDPOINT}`,
        authConfig
      );

      return normalizeTaskDraftMapPayload(unwrapResponsePayload(response));
    } catch {
      return {};
    }
  },

  saveTaskDraft: async (task_id: string, draft: TaskEditorState) => {
    const normalizedTaskId = task_id.trim();

    if (!normalizedTaskId) {
      return;
    }

    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_WORKING_DRAFT_SAVE_ENDPOINT}`,
      {
        task_id: normalizedTaskId,
        draft: {
          ...draft,
          task_id: normalizedTaskId,
        },
      },
      getTaskEditorAuthConfig()
    );
  },

  removeTaskDraft: async (task_id: string) => {
    const normalizedTaskId = task_id.trim();

    if (!normalizedTaskId) {
      return;
    }

    await axiosInstance.post<void>(
      `${TASK_EDITOR_DOMAIN}/${TASK_EDITOR_DRAFT_REMOVE_ENDPOINT}`,
      undefined,
      getTaskEditorAuthConfig({
        params: {
          task_id: normalizedTaskId,
        },
      })
    );
  },
};
